/**
 * ModelSelector - Custom dropdown for AI model selection
 * Properly styled for dark mode (native select dropdowns ignore CSS)
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui';

interface Model {
  id: string;
  name: string;
}

interface ModelSelectorProps {
  models: Model[];
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  disabled?: boolean;
}

export default function ModelSelector({ models, selectedModel, onModelChange, disabled }: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedModelData = models.find(m => m.id === selectedModel);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <Button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        variant="secondary"
        size="xs"
        className="gap-1.5"
        title="Select AI Model"
      >
        <svg className="w-3.5 h-3.5 text-warning" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span>{selectedModelData?.name || 'Select Model'}</span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-48 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          {models.map((model) => (
            <Button
              key={model.id}
              onClick={() => {
                onModelChange(model.id);
                setIsOpen(false);
              }}
              variant="ghost"
              className={`
                w-full justify-start px-3 py-2 text-sm h-auto rounded-none
                ${model.id === selectedModel
                  ? 'bg-info-light text-info'
                  : 'text-foreground'
                }
              `}
            >
              <div className="flex items-center gap-2">
                {model.id === selectedModel && (
                  <svg className="w-4 h-4 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className={model.id === selectedModel ? '' : 'ml-6'}>{model.name}</span>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

